import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { get, post, put, del } from '../utils/api';
import i18n from '../i18n';

import { useSuccessSnack, useErrorSnack } from '../utils/snackbar';

import { track } from '../utils/track';

function findLongPause(text) {
  let pauses = [];

  for (let i = 0; i < text.length; i++) {
    const curr = text[i];
    const next = text[i + 1];

    if (next && next.start - curr.end > 10) {
      pauses.push({
        start: curr.end,
        end: next.start,
        id: curr.id,
        idx: i,
      });
    }
  }
  return pauses;
}

export function formatMeeting(meeting) {
  if (!meeting?.text) {
    return meeting;
  }

  const text = meeting.text.map((x, idx) => ({
    ...x,
    text: x.text.replace(/<[^>]*>?/gm, ''),
  }));

  const longPause = findLongPause(text);

  return {
    ...meeting,
    text,
    longPause,
  };
}

async function getPendingMeetings() {
  const res = await get('/meetings/pending');
  return res?.data;
}

export async function getMeeting(id, isGuest = false) {
  if (!id) {
    return {};
  }

  track('get_meeting', { id, isGuest });
  const res = await get(`${isGuest ? '/guest' : ''}/meeting/${id}`);
  const data = res?.data?.meeting;
  if (data) {
    return formatMeeting(data);
  }
  return {};
}

async function updateMeeting(id, data) {
  track('update_meeting', { id });
  const res = await put(`/meeting/${id}`, data);
  return res?.data;
}

async function deleteMeeting(id) {
  track('delete_meeting', { id });
  const res = await del(`/meeting/${id}`);
  return res?.data;
}

async function runMeetingSegment(id, { from, to, autoSync, numSpeakers }) {
  const res = await post(
    `/meeting/${id}/run-segment`,
    {},
    { params: { from, to, auto_sync: autoSync, num_speakers: numSpeakers } }
  );
  return res?.data;
}

async function runMeetingSpeakerId(id, { numSpeakers, meetingBotSpeakers, overrideMeetingBotSpeakers }) {
  const res = await post(`/meeting/${id}/run-speaker-id`, {
    num_speakers: numSpeakers,
    meeting_bot_speakers: meetingBotSpeakers,
    override_meeting_bot_speakers: overrideMeetingBotSpeakers,
  });
  return res?.data;
}

export async function exportMeeting(
  id,
  {
    speaker = false,
    time = false,
    timeFormat = null,
    nl = 1,
    format = 'txt',
    collapseSegments = false,
    exportComments = false,
  }
) {
  track('export', {
    format,
    id,
    speaker,
    time,
    timeFormat,
    nl,
    collapseSegments,
    exportComments,
  });
  const res = await get(`/meeting/${id}/export`, {
    responseType: 'arraybuffer',
    params: {
      speaker: +speaker,
      time: +time,
      time_format: timeFormat,
      collapse_segments: +collapseSegments,
      export_comments: +exportComments,
      nl,
      format,
    },
  });
  return res?.data;
}

export async function getAudio(id) {
  const res = await get(`/meeting/${id}/audio`, {
    responseType: 'blob',
  });
  return res?.data;
}

export async function getPoster(
  id,
  { width, height, fit, isGuest = false } = {}
) {
  try {
    const res = await get(`${isGuest ? '/guest' : ''}/meeting/${id}/poster`, {
      params: { width, height, fit },
      responseType: 'blob',
    });

    if (!res.data.size) return null;

    return res?.data;
  } catch (err) {
    return null;
  }
}

export async function getMedia(id) {
  const res = await get(`/meeting/${id}/media`, { responseType: 'blob' });
  const ext = res.headers['content-type'].split('/')[1];
  return { data: res.data, ext };
}

export async function unlockMeeting(id) {
  const res = await post(`/meetings/${id}/unlock`);
  return res?.data;
}

export async function translateMeeting(id, language) {
  const res = await post(`/meeting/${id}/translate`, { language });
  return res?.data;
}

export async function updateMeetingTextAnalysis(id, data) {
  const res = await put(`/meeting/${id}/text-analysis`, data);
  return res?.data;
}

export function usePendingMeetingsQuery() {
  const { data } = useQuery(['pending_meetings'], () => getPendingMeetings());
  return {
    meetings: data,
  };
}

export function useMeetingQuery(id, isGuest) {
  const {
    data: meetingData,
    error,
    isLoading,
    isError,
  } = useQuery(['meeting', id], () => getMeeting(id, isGuest), {
    fetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: (data) => {
      const status = data?.status;
      const reqUserRole = data?.req_user_role;

      if (status === 'reviewing' && reqUserRole !== 'admin') {
        return 5000;
      }

      if (
        data?.status !== 'ready' &&
        data?.status !== 'failed' &&
        data?.status !== 'reviewing'
      ) {
        return 5000;
      }

      if (data?.summary_pending) {
        return 5000;
      }

      return 0;
    }, //polling
    keepPreviousData: true,
    retry: false,
    enabled: !!id,
  });

  const meeting = useMemo(() => meetingData || {}, [meetingData]);

  const meetingExists = useMemo(() => error?.response?.status !== 404, [error]);

  const accessDenied = useMemo(() => error?.response?.status === 403, [error]);

  return {
    meeting,
    isLoading,
    meetingExists,
    accessDenied,
    isError,
  };
}

export function useMeetingActions(id, { updateReplace = true } = {}) {
  const [revokedAssignedError, setRevokedAssignedError] = useState(false);
  const client = useQueryClient();

  const {
    mutate: _updateMeeting,
    mutateAsync: updateMeetingAsync,
    isSuccess,
    isError,
    isLoading: isLoadingUpdate,
  } = useMutation((data) => updateMeeting(id, data), {
    onSettled: (data, error, vars) => {
      if (!error) {
        setRevokedAssignedError(false);
        if (updateReplace) {
          client.setQueryData(['meeting', id], formatMeeting(data.meeting));
        }

        if (vars.old_folder_prefix) {
          // Bit ugly but works. invalidates a bunch of info if a meeting is moved
          client.invalidateQueries('organization');
        }
        client.invalidateQueries('folderAssets');
      } else {
        setRevokedAssignedError(!!error?.response?.data?.missing_assigned);
      }
    },
  });

  useSuccessSnack(i18n.t('Transkribering uppdaterad'), isSuccess);
  useErrorSnack(
    i18n.t(
      'Kunde inte spara din uppdatering. Var god ladda om sidan om problemet kvarstår'
    ),
    isError
  );

  const { mutate: _exportMeeting, mutateAsync: exportMeetingAsync } =
    useMutation((options) => exportMeeting(id, options), {
      onSettled: () => {
        client.invalidateQueries('credits');
      },
    });

  const { mutate: _getMedia, mutateAsync: getMediaAsync } = useMutation(() =>
    getMedia(id)
  );

  const {
    mutate: _deleteMeeting,
    mutateAsync: deleteMeetingAsync,
    isSuccess: deleteSuccess,
    isError: deleteError,
    isLoading: isDeleting,
  } = useMutation(() => deleteMeeting(id), {
    onSettled: () => {
      client.invalidateQueries('meetings');
      client.invalidateQueries('folderAssets');
      client.invalidateQueries('organization');
      client.invalidateQueries(['meeting', id]);
    },
  });

  useSuccessSnack(i18n.t('Transkribering borttagen'), deleteSuccess);
  useErrorSnack(
    i18n.t('Fel uppstod vid borttagning av transkribering'),
    deleteError
  );

  const { mutate: runSegment, mutateAsync: runSegmentAsync } = useMutation(
    (data) => runMeetingSegment(id, data),
    {
      onSettled: () => {
        //client.invalidateQueries('meetings');
        client.invalidateQueries(['meeting', id]);
      },
    }
  );

  const { mutate: runSpeakerId, mutateAsync: runSpeakerIdAsync } = useMutation(
    (data) => runMeetingSpeakerId(id, data),
    {
      onSettled: () => {
        client.invalidateQueries(['meeting', id]);
      },
    }
  );

  const { mutate: _unlockMeeting, mutateAsync: unlockMeetingAsync } =
    useMutation(() => unlockMeeting(id), {
      onSuccess: (data) => {
        client.setQueryData(['meeting', id], formatMeeting(data));
      },
    });

  const {
    mutate: _translateMeeting,
    mutateAsync: translateMeetingAsync,
    isError: translateError,
    isSuccess: translateSuccess,
  } = useMutation(({ language }) => translateMeeting(id, language), {
    onSuccess: (data) => {},
  });

  useSuccessSnack(i18n.t('Översättning påbörjad'), translateSuccess);
  useErrorSnack(i18n.t('Fel uppstod vid översättning'), translateError);

  const {
    mutate: _updateMeetingTextAnalysis,
    mutateAsync: updateMeetingTextAnalysisAsync,
    isError: updateMeetingTextAnalysisError,
    isSuccess: updateMeetingTextAnalysisSuccess,
  } = useMutation(
    (textAnalysis) => updateMeetingTextAnalysis(id, textAnalysis),
    {
      onSuccess: (data) => {
        client.invalidateQueries(['meeting', id]);
      },
    }
  );

  useSuccessSnack(
    i18n.t('Sammanfattning uppdaterad'),
    updateMeetingTextAnalysisSuccess
  );
  useErrorSnack(
    i18n.t('Fel uppstod vid uppdatering av sammanfattning'),
    updateMeetingTextAnalysisError
  );

  return {
    updateMeeting: _updateMeeting,
    exportMeeting: _exportMeeting,
    exportMeetingAsync,
    updateMeetingAsync,
    deleteMeeting: _deleteMeeting,
    deleteMeetingAsync,
    isDeleting,
    runSegment,
    runSegmentAsync,
    updateMeetingError: isError,
    runSpeakerId,
    runSpeakerIdAsync,
    revokedAssignedError,
    isLoadingUpdate,
    unlockMeeting: _unlockMeeting,
    unlockMeetingAsync,
    translateMeeting: _translateMeeting,
    translateMeetingAsync,
    updateMeetingTextAnalysis: _updateMeetingTextAnalysis,
    updateMeetingTextAnalysisAsync,
    getMedia: _getMedia,
    getMediaAsync,
  };
}

async function getMeetingQueue(id) {
  const res = await get(`/queue/${id}`);
  return res.data;
}

export function useMeetingQueueQuery(id) {
  const { data } = useQuery(['meetingQueue', id], () => getMeetingQueue(id), {
    enabled: !!id,
    refetchInterval: 10000,
  });

  return {
    queue: data,
  };
}
